/**
 * Backorder Notification
 * Shows a message when customer adds more items than available stock
 * for products that allow continue selling when out of stock
 */

class BackorderNotification {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('theme:product:add', this.handleProductAdd.bind(this));
  }

  handleProductAdd(event) {
    const { response, button } = event.detail;

    if (!response || !button) return;

    // Get variant data
    const variantMetafieldsEl = document.querySelector('[data-variant-metafields]');
    if (!variantMetafieldsEl) return;

    let variantMetafields;
    try {
      variantMetafields = JSON.parse(variantMetafieldsEl.textContent);
    } catch (e) {
      return;
    }

    // Get the variant ID and quantity from the response (cart item that was just added)
    const variantId = response.variant_id || response.id;
    const requestedQty = response.quantity;

    if (!variantId || !requestedQty) return;

    // Get variant metadata
    const variantData = variantMetafields[variantId];
    if (!variantData) return;

    // Check if this variant allows continue selling (stock override = false)
    const stockOverride = variantData.exo_out_of_stock_xtra_field;

    // Only show notification if stock override is false (continue selling enabled)
    if (stockOverride === false) {
      // Disable only display primary location inventory
      // // Use inventory_quantity as the source of truth for total available inventory
      // const availableQty = variantData.inventory_quantity || 0;

      // Calculate total inventory from all store locations
      let availableQty = 0;

      // Get store inventory metafields directly from variantData
      const storeMetafields = [
        variantData.inventory_store_1,
        variantData.inventory_store_2,
        variantData.inventory_store_3,
        variantData.inventory_store_4,
        variantData.inventory_store_5,
        variantData.inventory_store_6,
        variantData.inventory_store_7,
        variantData.inventory_store_8
      ];

      // Sum up all store inventories from inventory_levels
      storeMetafields.forEach((storeData) => {
        if (storeData && storeData.inventory_levels && Array.isArray(storeData.inventory_levels)) {
          storeData.inventory_levels.forEach(level => {
            if (typeof level.available === 'number') {
              availableQty += level.available;
            }
          });
        }
      });

      // Fall back to total_inventory if calculation yielded 0
      if (availableQty === 0 && typeof variantData.total_inventory !== 'undefined' && variantData.total_inventory !== null && variantData.total_inventory > 0) {
        availableQty = variantData.total_inventory;
      }
      // Fall back to inventory_quantity (primary location)
      else if (availableQty === 0 && typeof variantData.inventory_quantity !== 'undefined' && variantData.inventory_quantity !== null && variantData.inventory_quantity >= 0) {
        availableQty = variantData.inventory_quantity;
      }
      // Last resort: assume 0
      else if (availableQty === 0 && variantData.inventory_management === 'shopify') {
        availableQty = 0;
      }

      if (requestedQty > availableQty && availableQty > 0) {
        // Calculate backorder quantity
        const backorderQty = requestedQty - availableQty;
        this.showBackorderNotification(availableQty, backorderQty);
      } else if (availableQty <= 0) {
        // All items are on backorder
        this.showBackorderNotification(0, requestedQty);
      }
    }
  }

  showBackorderNotification(inStockQty) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'backorder-notification';
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');

    let message;
    if (inStockQty > 0) {
      message = `We have ${inStockQty} of this item in stock. We will contact you with an ETA for the remaining stock if you proceed to order this quantity.`;
    } else {
      message = `We are currently out of stock of this item but we may be able to get it quickly for you. If you proceed to order it we will contact you with an ETA.`;
    }

    notification.innerHTML = `
      <div class="backorder-notification__content">
        <svg class="backorder-notification__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V9H11V15ZM11 7H9V5H11V7Z" fill="currentColor"/>
        </svg>
        <p class="backorder-notification__message">${message}</p>
        <button class="backorder-notification__close" aria-label="Close notification">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    `;

    // Add close functionality
    const closeButton = notification.querySelector('.backorder-notification__close');
    closeButton.addEventListener('click', () => {
      notification.classList.add('backorder-notification--hiding');
      setTimeout(() => {
        notification.remove();
      }, 300);
    });

    // Add to page
    const errorContainer = document.querySelector('[data-cart-errors-container]');
    if (errorContainer) {
      // Insert after error container
      errorContainer.parentNode.insertBefore(notification, errorContainer.nextSibling);
    } else {
      // Fallback: add to form
      const form = document.querySelector('[data-product-form]');
      if (form) {
        form.appendChild(notification);
      }
    }

    // Auto-hide after 10 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.classList.add('backorder-notification--hiding');
        setTimeout(() => {
          notification.remove();
        }, 300);
      }
    }, 10000);

    // Trigger animation
    requestAnimationFrame(() => {
      notification.classList.add('backorder-notification--visible');
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BackorderNotification();
  });
} else {
  new BackorderNotification();
}
